/**
 * "Já li este capítulo" — guardado NESTE navegador.
 *
 * É conveniência pessoal, não dado de negócio: perder a marca (aba anônima, navegador
 * limpo) custa um clique, e por isso não vale uma tabela no servidor. Todo acesso ao
 * `localStorage` passa por try/catch: em modo privado do Safari, em prévia de link ou com
 * cookies bloqueados o acessor LANÇA, e a tela precisa continuar abrindo — só sem a marca.
 */
import { useCallback, useEffect, useState } from 'react';

const CHAVE = 'mgdlead:guia:lidos';
const EVENTO = 'mgdlead:guia:lidos-mudou';

export function lerLidos(): ReadonlySet<string> {
  try {
    const bruto = window.localStorage.getItem(CHAVE);
    if (!bruto) return new Set();
    const lista: unknown = JSON.parse(bruto);
    return new Set(
      Array.isArray(lista) ? lista.filter((x): x is string => typeof x === 'string') : [],
    );
  } catch {
    return new Set();
  }
}

function gravarLidos(lidos: ReadonlySet<string>): void {
  try {
    window.localStorage.setItem(CHAVE, JSON.stringify([...lidos]));
  } catch {
    /* Sem armazenamento: a marca vale só enquanto a aba estiver aberta. */
  }
  // Avisa as outras partes da MESMA aba (o evento `storage` só chega nas outras abas).
  window.dispatchEvent(new Event(EVENTO));
}

/** Os capítulos lidos, reagindo a mudança feita em outro componente ou em outra aba. */
export function useCapitulosLidos() {
  const [lidos, setLidos] = useState<ReadonlySet<string>>(() => lerLidos());

  useEffect(() => {
    const atualizar = () => setLidos(lerLidos());
    window.addEventListener(EVENTO, atualizar);
    window.addEventListener('storage', atualizar);
    return () => {
      window.removeEventListener(EVENTO, atualizar);
      window.removeEventListener('storage', atualizar);
    };
  }, []);

  const marcar = useCallback((id: string, lido: boolean) => {
    const proximo = new Set(lerLidos());
    if (lido) proximo.add(id);
    else proximo.delete(id);
    setLidos(proximo);
    gravarLidos(proximo);
  }, []);

  return { lidos, marcar };
}
